import React, { useEffect } from 'react';

const Docs = () => {
  useEffect(() => {
    document.title = 'Multicrop - Documentation';
  }, []);

  return (
    <div style={styles.container}>
      <h1 style={styles.header}>Multicrop — Documentation</h1>

      <section style={styles.section}>
        <h2>Overview</h2>
        <p>
          Multicrop is a small browser-based utility for creating multiple cropped
          versions of a single image. It provides manual and "smart" cropping
          workflows, flexible toolbar controls for zooming and framing, and a
          downloadable gallery of exported crops.
        </p>
      </section>

      <section style={styles.section}>
        <h2>Key features</h2>
        <ul>
          <li>Drag or click to upload an image to get started.</li>
          <li>Draw crop frames directly on the image (Draw Frames mode).</li>
          <li>Enter exact dimensions to add frames programmatically.</li>
          <li>Smart Frames: use object/face detection to generate suggested crops.</li>
          <li>Zoom and pan the image; the toolbar is draggable for convenience.</li>
          <li>Export multiple cropped images and download selected or all.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>Quick workflow</h2>
        <ol>
          <li>Open the app ("App" page) and upload or drag an image onto the canvas.</li>
          <li>Use the toolbar to zoom in/out and to center the image if needed.</li>
          <li>Add crop frames using one of the toolbar options:
            <ul>
              <li><strong>Draw Frames</strong> — draw freehand frames on the image.</li>
              <li><strong>Smart Frames</strong> — open the Smart Crop dialog, enter desired widths/heights, optionally use the Focus Area to target a region, then generate smart crops.</li>
              <li><strong>Enter Dimensions</strong> — add frames with exact pixel sizes.</li>
            </ul>
          </li>
          <li>Move or resize frames; double-click a frame to cycle focus if frames overlap.</li>
          <li>When satisfied, use the Crop → Make New Image Crops to generate exports.</li>
          <li>Open the Downloads panel to review, select, delete, or download crops.</li>
        </ol>
      </section>

      <section style={styles.section}>
        <h2>Toolbar reference</h2>
        <dl>
          <dt>Zoom</dt>
          <dd>Buttons to zoom in, zoom out, and reset zoom. The current zoom percentage is shown.</dd>
          <dt>Add Crop Frames</dt>
          <dd>Provides three sub-actions: Draw Frames, Smart Frames, Enter Dimensions.</dd>
          <dt>Crop</dt>
          <dd>Make new image crops from frames, or open the downloads panel to manage exports.</dd>
          <dt>Clear Image</dt>
          <dd>Remove the current image and all frames/exports.</dd>
        </dl>
      </section>

      <section style={styles.section}>
        <h2>Smart Frames & Focus Area</h2>
        <p>
          The Smart Frames flow uses object detection to propose crops. When you open
          Smart Frames you can:
        </p>
        <ul>
          <li>Enter a list of desired dimensions (one per line or comma-separated, formats like <code>256x256</code> or <code>512</code>).</li>
          <li>Show the Focus Area (via the Show Focus Area button in the Smart Frames dialog). The Focus Area is an editable rectangle you can drag and resize. While editing the Focus Area, only the image and the Show/Hide Focus Area toggle are visible so you can focus on positioning.
          </li>
          <li>Click Smart Crop to generate frames based on detected objects or the Focus Area if provided.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>Crop frames behavior</h2>
        <ul>
          <li>Frames have an original width/height (used when exporting) and can be resized interactively.</li>
          <li>Dragging or resizing a frame updates its preview in real time.</li>
          <li>Use the label edit controls to enter exact dimensions for a frame.</li>
          <li>Top-right resize is intentionally disabled (by design).</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>Downloads & export</h2>
        <p>After creating crops, use the Downloads panel to:</p>
        <ul>
          <li>Select individual crops (checkbox) or use Select All/None.</li>
          <li>Delete selected or delete all generated crops.</li>
          <li>Download individual crops or download all at once.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>Keyboard & accessibility</h2>
        <ul>
          <li><strong>Esc</strong> or <strong>Enter</strong> will exit Draw Frames mode.</li>
          <li>Double-click the toolbar handle to recenter it horizontally.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>Troubleshooting & build notes</h2>
        <p>
          If you see build failures on CI (Netlify, Vercel), common causes include ESLint hooks warnings (Create React App treats them as errors in CI) or missing dev dependencies. Example fixes:
        </p>
        <ul>
          <li>Run the production build locally with <code>CI=true npm run build</code> to reproduce CI errors.</li>
          <li>Fix any react-hooks/exhaustive-deps warnings by including the appropriate dependencies in effect arrays.</li>
          <li>Set <code>GENERATE_SOURCEMAP=false</code> in CI env vars to avoid sourcemap parse warnings from some third-party packages.</li>
        </ul>
      </section>

      <section style={styles.section}>
        <h2>FAQ</h2>
        <dl>
          <dt>Q: My focus area disappears while I edit it — how to keep it visible?</dt>
          <dd>A: Use the Show Focus Area toggle in the Smart Frames dialog. The Focus Area is persistent while toggled on; it will remain visible even if the dialog loses focus.</dd>
          <dt>Q: The exported images look low-res — what should I do?</dt>
          <dd>A: Make sure the original frame's "originalWidth"/"originalHeight" are set correctly. Smart crops attempt to preserve aspect and provide best-effort upscaling using Pica for better results.</dd>
        </dl>
      </section>

      <section style={styles.section}>
        <h2>Contact & source</h2>
        <p>Source code and issues are tracked in the repository. For questions or contributions, open an issue or pull request.</p>
      </section>
    </div>
  );
};

const styles = {
  container: {
    padding: '28px',
    maxWidth: '1100px',
    margin: '0 auto',
    color: 'rgba(255,255,255,0.88)',
    background: 'rgba(10,10,12,0.6)',
    borderRadius: '8px',
    boxSizing: 'border-box'
  },
  header: {
    fontSize: '4rem',
    marginBottom: '1.5rem',
    fontFamily: 'lunchtype22light'
  },
  section: {
    marginBottom: '1.8rem'
  }
};

export default Docs;
